import { TKV2_ORDER_API, ToolkitOrder, expected, isDefined } from '@aloft/toolkit-shared';
import { useQueryClient } from '@tanstack/react-query';

import { useEffect } from 'react';

import { useUpdateTkv2OrderAPI } from '../../hooks/api/toolkitComponents.ts';
import { useEdgeTrigger } from '../../hooks/useEdgeTrigger.ts';
import { orderDetailsMutationComplete } from '../../redux/features/orderSlice.ts';
import {
  selectCostMultiplier,
  selectEffectiveDate,
  selectIntendedUse,
  selectIntendedUsers,
  selectStartedAt,
} from '../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { DEFAULT_SYNC_DRIVER_DELAY_MS } from './consts.ts';

export function useOrderSyncDriver(orderID: string, orderData?: ToolkitOrder) {
  const dispatch = useAppDispatch();
  const { mutate: orderMutate } = useUpdateTkv2OrderAPI();

  const { editRevision: orderEditRevision = 0, serverRevision: orderServerRevision = 0 } =
    useAppSelector((state) => state.order.detailsSync) ?? {};

  // right now we can only update the cost multiplier, but in the future we
  // could use order details syncing for other things on the order like
  // effective date.

  const costMultiplier = useAppSelector(selectCostMultiplier);
  const intendedUse = useAppSelector(selectIntendedUse);
  const intendedUsers = useAppSelector(selectIntendedUsers);
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const startedAt = useAppSelector(selectStartedAt);
  const qc = useQueryClient();

  const onOrderMutateSuccess = () => {
    dispatch(
      orderDetailsMutationComplete({
        status: 'success',
        serverRevision: orderEditRevision,
      }),
    );
    qc.invalidateQueries(['api', 'v2', 'orders']);
  };

  const onOrderMutateError = () => {
    dispatch(
      orderDetailsMutationComplete({
        status: 'error',
      }),
    );
  };

  const mutateOrder = () => {
    const { isArchived, isDeleted, input } = expected(orderData);

    orderMutate(
      {
        pathParams: { orderID },
        body: TKV2_ORDER_API.PUT.REQUEST.BODY.parse({
          isArchived,
          isDeleted,
          input: {
            ...input,
            costMultiplier,
            effectiveDate,
          },
          intendedUse,
          intendedUsers,
          startedAt,
        }),
      },
      { onSuccess: onOrderMutateSuccess, onError: onOrderMutateError },
    );
  };

  const orderMutationTrigger = useEdgeTrigger({
    condition: isDefined(orderData) && orderEditRevision > orderServerRevision,
    delayMs: DEFAULT_SYNC_DRIVER_DELAY_MS,
    action: mutateOrder,
  });

  useEffect(() => {
    orderMutationTrigger.restartTimer();
  }, [orderEditRevision, orderMutationTrigger]);
}
